import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { validateEnvironmentOnStartup } from './core/utils/env';
import { ErrorTracker } from './core/utils/errorTracking';
import { setupHMRDebugger, checkHMRHealth } from './core/utils/hmrDebug';

// Validate environment variables before starting the app
validateEnvironmentOnStartup();

// Start error tracking
ErrorTracker.initialize();

// Setup HMR debugging in development
if (import.meta.env.DEV) {
  setupHMRDebugger();
  checkHMRHealth();

  // Enable runtime detection of forbidden Date usage
  import('./core/utils/unified-date').then(({ detectForbiddenDateUsage }) => {
    detectForbiddenDateUsage();
  });
}

import { initTabSwitchFix } from './utils/tabSwitchFix';

// Start React app
const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);

// Initialize tab switch fix
initTabSwitchFix();

// Handle HMR for better hot reload experience
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    // Re-render the app on hot reload
    root.render(<App />);
  });
}

// Remove all the old complex navigation timeout code and cleanup
// The new tabSwitchFix handles everything more simply

// Handle chunk loading errors in production (when assets change after deployment)
if (!import.meta.env.DEV) {
  // Global error handler for chunk loading failures - DISABLED to prevent infinite reloads
  // Keeping the listeners but only logging errors, not reloading
  window.addEventListener('error', event => {
    const { message, filename, error } = event;

    // Check if this is a chunk loading error
    if (
      message?.includes('Loading chunk') ||
      message?.includes('ChunkLoadError') ||
      message?.includes('Failed to fetch dynamically imported module') ||
      message?.includes('Loading CSS chunk') ||
      message?.includes('Failed to import') ||
      filename?.includes('assets/') ||
      error?.name === 'ChunkLoadError'
    ) {
      // Bootstrap-time handler: runs before module init is safe for Logger side effects
      console.warn('Chunk loading failed, please refresh manually:', { message, filename, error });
      // DO NOT auto-reload - was causing infinite reload loops
    }
  });

  // Handle unhandled promise rejections (for dynamic imports)
  window.addEventListener('unhandledrejection', event => {
    const { reason } = event;

    if (
      reason?.message?.includes('Loading chunk') ||
      reason?.message?.includes('ChunkLoadError') ||
      reason?.message?.includes('Failed to fetch dynamically imported module') ||
      reason?.message?.includes('Loading CSS chunk') ||
      reason?.message?.includes('Failed to import') ||
      reason?.name === 'ChunkLoadError' ||
      (reason?.stack && reason.stack.includes('import('))
    ) {
      // Bootstrap-time handler: runs before module init is safe for Logger side effects
      console.warn('Dynamic import failed, please refresh manually:', reason);
      event.preventDefault(); // Prevent the error from being logged
      // DO NOT auto-reload - was causing infinite reload loops
    }
  });
}
